<template>
    <div class="max-w-xl mx-auto">
        <div class="my-4">
            <div class="text-darkGrayishBlue text-xl text-center mb-4">
                {{ $t('register.greet') }}
            </div>
            <div class="border border-darkGrayishBlue rounded">
                <div class="py-8 px-4 sm:p-8 text-lg">
                    <formFieldRow
                        :name="['surname', 'givenName']"
                        :label="$t('register.name')"
                        labelWidthClass="sm:w-24"
                        :errors="errors"
                    >
                        <template #default="{hasError}">
                            <div class="flex flex-col sm:flex-row items-start">
                                <input
                                    class="form-input w-full mb-2 sm:w-32 sm:mr-2 sm:mb-0"
                                    :class="{ 'border-red': hasError }"
                                    type="text"
                                    v-model.trim="form.surname"
                                    :placeholder="$t('account.surname')"
                                />
                                <input
                                    class="form-input w-full sm:flex-1"
                                    :class="{ 'border-red': hasError }"
                                    type="text"
                                    v-model.trim="form.givenName"
                                    :placeholder="$t('account.given.name')"
                                />
                            </div>
                        </template>
                    </formFieldRow>
                    <formFieldRow
                        name="email"
                        :label="$t('account.email')"
                        labelWidthClass="sm:w-24"
                        :errors="errors"
                    >
                        <template #default="{hasError}">
                            <input
                                class="form-input"
                                type="email"
                                :class="{ 'border-red': hasError }"
                                v-model.trim="form.email"
                                :disabled="!allowEditEmail"
                            />
                            <div class="text-blue">
                                {{ $t('account.email.ask.for.input') }}
                            </div>
                        </template>
                    </formFieldRow>
                    <div class="mt-2 mb-4">
                        <checkbox v-model="form.agreeTerms">
                            <message msgKey="register.agree">
                                <template #arg0>
                                    <button
                                        class="px-2 text-blue underline"
                                        @click="showTerms = true"
                                    >
                                        {{ $t('register.terms') }}
                                    </button>
                                    <termsModal
                                        v-if="showTerms"
                                        @close="showTerms = false"
                                    >
                                        <termsOfUse />
                                    </termsModal>
                                </template>
                                <template #arg1>
                                    <button
                                        class="px-2 text-blue underline"
                                        @click="showPrivacy = true"
                                    >
                                        {{ $t('register.privacy') }}
                                    </button>
                                    <termsModal
                                        v-if="showPrivacy"
                                        @close="showPrivacy = false"
                                    >
                                        <privacyPolicy />
                                    </termsModal>
                                </template>
                            </message>
                        </checkbox>
                        <div
                            v-if="errors.agreeTerms"
                            class="text-red text-sm ml-10"
                        >
                            {{ $t(errors.agreeTerms) }}
                        </div>
                    </div>
                    <div>
                        <checkbox v-model="form.agreePromotions">
                            {{ $t('account.agree.promotion') }}
                        </checkbox>
                    </div>
                </div>
            </div>
        </div>
        <errorBox :message="errors.server" />
        <div class="flex text-lg">
            <button
                class="btn w-1/2 mr-2 justify-center"
                :disabled="saveStatus !== undefined"
                @click="clearForm"
            >
                {{ $t('common.reset') }}
            </button>
            <loadingButton
                class="btn bg-primary w-1/2 ml-2 justify-center font-black"
                :status="saveStatus"
                @click="confirm"
                @successShown="$router.push({ name: 'home' })"
            >
                {{ $t('common.confirm') }}
            </loadingButton>
        </div>
    </div>
</template>

<script>
import errorBox from '@/components/errorBox.vue';
import checkbox from '@/components/checkbox.vue';
import { message } from '@/utils/getMessage';
import formFieldRow from '@/components/formFieldRow.vue';
import loadingButton from '@/components/loadingButton.vue';
import pageLoader from '@/components/pageLoader.vue';
import termsModal from '@/components/termsModal.vue';
import { instance } from '@/utils/ajax';
import { validate } from '@/utils/validationUtil';
async function confirmTermsOfUse(form) {
    try {
        let result = await instance.post(
            '/api/accountInfo/confirmTermsOfUse',
            form
        );
        return result.data;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error confirmTermsOfUse');
        return { success: false, all: 'error.connect' };
    }
}

export default {
    components: {
        checkbox,
        message,
        formFieldRow,
        loadingButton,
        errorBox,
        termsModal,
        termsOfUse: () => ({
            loading: pageLoader,
            component: import('@/components/termsOfUseContent.vue')
        }),
        privacyPolicy: () => ({
            loading: pageLoader,
            component: import('@/components/privacyPolicyContent.vue')
        })
    },
    data() {
        const u = this.$store.state.user;

        return {
            form: {
                surname: u.custLastName,
                givenName: u.custFrstName,
                email: u.custEmailAddr,
                agreeTerms: true,
                agreePromotions: true
            },
            errors: {},
            saveStatus: undefined,
            showTerms: false,
            showPrivacy: false
        };
    },
    computed: {
        allowEditEmail() {
            return this.$store.state.user?.emailAddrVrfyInd !== 'Y';
        }
    },
    methods: {
        clearForm() {
            this.form = {
                surname: undefined,
                givenName: undefined,
                email: this.allowEditEmail ? undefined : this.form.email,
                agreeTerms: true,
                agreePromotions: true
            };
            this.errors = {};
        },
        async confirm() {
            this.errors = {};
            const v = validate(this.errors, this.form, {
                surname: { required: true },
                givenName: { required: true },
                agreeTerms: {
                    checked: function(val) {
                        if (val !== true) {
                            return 'register.error.agree.terms';
                        }
                    }
                }
            });
            if (v.hasError) {
                this.errors = v.errors;
                return;
            }

            this.saveStatus = 'loading';
            const result = await confirmTermsOfUse(this.form);
            if (result.success) {
                this.saveStatus = 'success';
                this.$store.commit('setUser', result.data.user);
            } else {
                this.saveStatus = undefined;
                this.errors = result.data.errors;
            }
        }
    }
};
</script>
